import React, { useState, useEffect, useContext } from "react";
import { TranslatorContext } from "../../../context/Translator";
import axios from "axios";
import { Modal, Row, Col, Table, Card, Form, Button } from "react-bootstrap";
import PageLayout from "../../../layouts/PageLayout";
import { FaSearch, FaPrint, FaChartLine, FaFilter, FaTimes, FaChevronDown, FaChevronUp, FaPhone, FaBox, FaStore } from "react-icons/fa";
import Select from 'react-select';

export default function VendorReport() {
    const { t } = useContext(TranslatorContext);
    const [loading, setLoading] = useState(false);
    const [report, setReport] = useState([]);
    const [vendors, setVendors] = useState([]);
    const [selectedVendor, setSelectedVendor] = useState(null);
    const [selectedMonth, setSelectedMonth] = useState(new Date());
    const [showFilters, setShowFilters] = useState(true);
    const [expandedVendors, setExpandedVendors] = useState({});
    const [expandedProducts, setExpandedProducts] = useState({});
    const [hasSearched, setHasSearched] = useState(false);

    const vendorOptions = vendors.map(vendor => ({
        value: vendor.id,
        label: `${vendor.company_name} - ${vendor.phone || ''}`
    }));

    const monthOptions = [
        { value: '01', label: 'January' },
        { value: '02', label: 'February' },
        { value: '03', label: 'March' },
        { value: '04', label: 'April' },
        { value: '05', label: 'May' },
        { value: '06', label: 'June' },
        { value: '07', label: 'July' },
        { value: '08', label: 'August' },
        { value: '09', label: 'September' },
        { value: '10', label: 'October' },
        { value: '11', label: 'November' },
        { value: '12', label: 'December' }
    ];

    const yearOptions = () => {
        const currentYear = new Date().getFullYear();
        const years = [];
        for (let i = currentYear - 2; i <= currentYear + 1; i++) {
            years.push({ value: i, label: i.toString() });
        }
        return years;
    };

    const [selectedYear, setSelectedYear] = useState({ value: new Date().getFullYear(), label: new Date().getFullYear().toString() });

    // Initial vendors fetch (without report)
    useEffect(() => {
        fetchVendors();
    }, []);

    const handlemonthChange = (option) => {

        if (option) {

            const month = option.value.padStart(2, '0');

            const newDate = new Date(selectedMonth);

            newDate.setMonth(parseInt(option.value) - 1);

            setSelectedMonth(newDate);

            fetchVendors(month);
        }
    };



const fetchVendors = async (month) => {
    setLoading(true);

    const params = {

        month: month?.toString().padStart(2, '0'),

    };
    try {
        const res = await axios.get(`${process.env.REACT_APP_API_URL}/initial-vendor-report`, {
            params: params,
            headers: { Authorization: `Bearer ${localStorage.getItem("authToken")}` }
        });
        setVendors(res.data.vendors || []);
    } catch (err) {
        console.error(err);
    } finally {
        setLoading(false);
    }
};

const fetchReport = async () => {
    if (!selectedVendor) {
        alert('Please select a vendor first');
        return;
    }

    setLoading(true);
    try {
        const month = selectedMonth.getMonth() + 1;
        const year = selectedYear.value;

        const params = {
            vendor_id: selectedVendor.value,
            month: month.toString().padStart(2, '0'),
            year: year
        };

        const res = await axios.get(`${process.env.REACT_APP_API_URL}/vendor-report`, {
            params: params,
            headers: { Authorization: `Bearer ${localStorage.getItem("authToken")}` }
        });

        setReport(res.data.report || []);
        setHasSearched(true);

        // Expand the selected vendor
        const expanded = {};
        if (res.data.report && res.data.report.length > 0) {
            expanded[res.data.report[0].vendor_id] = true;
        }
        setExpandedVendors(expanded);
        setExpandedProducts({});
    } catch (err) {
        console.error(err);
    } finally {
        setLoading(false);
    }
};

const handleSearch = () => {
    fetchReport();
};

const handleReset = () => {
    setSelectedVendor(null);
    setSelectedMonth(new Date());
    setSelectedYear({ value: new Date().getFullYear(), label: new Date().getFullYear().toString() });
    setReport([]);
    setExpandedVendors({});
    setExpandedProducts({});
    setHasSearched(false);
};

const toggleVendor = (vendorId) => {
    setExpandedVendors(prev => ({
        ...prev,
        [vendorId]: !prev[vendorId]
    }));
};

const toggleProduct = (vendorId, productName) => {
    const key = `${vendorId}_${productName}`;
    setExpandedProducts(prev => ({
        ...prev,
        [key]: !prev[key]
    }));
};

const formatCurrency = (amount) => {
    if (amount === undefined || amount === null) return '0';
    return new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    }).format(amount);
};

const exportToPDF = () => {
    if (!selectedVendor) {
        alert('Please select a vendor first');
        return;
    }
    const month = selectedMonth.getMonth() + 1;
    const year = selectedYear.value;
    let url = `${process.env.REACT_APP_URL}/report/vendor-report/pdf`;
    const params = new URLSearchParams();
    params.append("vendor_id", selectedVendor.value);
    params.append("month", month.toString().padStart(2, '0'));
    params.append("year", year);
    const finalUrl = `${url}?${params.toString()}`;
    window.open(finalUrl, "_blank");
};

// Calculate totals for summary table
const calculateSummaryTotals = (vendor) => {
    let totalPurchase = 0;
    let totalSale = 0;
    let totalProfit = 0;

    vendor.summary?.forEach(item => {
        totalPurchase += item.purchase_price || 0;
        totalSale += item.sale_price || 0;
        totalProfit += item.profit || 0;
    });

    return { totalPurchase, totalSale, totalProfit };
};

return (
    <PageLayout>
        <div className="mb-4">
            <h2 className="mb-1 fw-bold text-dark">
                <FaChartLine className="me-2 text-primary" />
                {t('Vendor Report')}
            </h2>
            <p className="text-muted mb-0 small">{t('View vendor purchase and sales performance')}</p>
        </div>

        {/* Filter Section */}
        <Card className="shadow-sm mb-4 border-0">
            <Card.Body className="p-3 p-md-4">
                <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
                    <h5 className="mb-0 d-flex align-items-center gap-2 fs-6 fw-bold text-primary">
                        <FaFilter size={14} />
                        {t('Filters')}
                    </h5>
                    <Button
                        variant="link"
                        onClick={() => setShowFilters(!showFilters)}
                        className="text-decoration-none p-0"
                        size="sm"
                    >
                        {showFilters ? <FaTimes /> : <FaFilter />}
                    </Button>
                </div>

                {showFilters && (
                    <Row className="g-3 align-items-end">

                        <Col xs={12} md={3}>
                            <Form.Label className="fw-semibold small mb-1">{t('Select Month')}</Form.Label>
                            <Select
                                options={monthOptions}
                                value={monthOptions.find(m => m.value === (selectedMonth.getMonth() + 1).toString().padStart(2, '0'))}
                                onChange={handlemonthChange}
                                placeholder={t('Select month')}
                                className="react-select-container"
                                classNamePrefix="react-select"
                            />
                        </Col>
                        <Col xs={12} md={3}>
                            <Form.Label className="fw-semibold small mb-1">{t('Select Year')}</Form.Label>
                            <Select
                                options={yearOptions()}
                                value={selectedYear}
                                onChange={setSelectedYear}
                                placeholder={t('Select year')}
                                className="react-select-container"
                                classNamePrefix="react-select"
                            />
                        </Col>
                        <Col xs={12} md={4}>
                            <Form.Label className="fw-semibold small mb-1">{t('Select Vendor')} *</Form.Label>
                            <Select
                                options={vendorOptions}
                                value={selectedVendor}
                                onChange={setSelectedVendor}
                                placeholder={t('Select a vendor...')}
                                className="react-select-container"
                                classNamePrefix="react-select"
                                isClearable={true}
                            />
                        </Col>
                        <Col xs={12} md={2}>
                            <div className="d-flex gap-2">
                                <Button
                                    variant="primary"
                                    onClick={handleSearch}
                                    className="flex-grow-1 d-flex align-items-center justify-content-center gap-2"
                                    size="sm"
                                    disabled={!selectedVendor}
                                >
                                    <FaSearch size={12} />
                                    {t('Search')}
                                </Button>
                                <Button
                                    variant="secondary"
                                    onClick={handleReset}
                                    size="sm"
                                >
                                    {t('Reset')}
                                </Button>
                            </div>
                        </Col>
                    </Row>
                )}
            </Card.Body>
        </Card>

        {/* Action Buttons - Only show when report exists */}
        {hasSearched && report.length > 0 && selectedVendor && (
            <div className="mb-3 d-flex justify-content-end no-print">
                <Button variant="danger" onClick={exportToPDF} className="rounded-3 d-flex align-items-center gap-2" size="sm">
                    <FaPrint size={14} />
                    {t('Export to PDF')}
                </Button>
            </div>
        )}

        {/* Report Section - Only show after search */}
        {hasSearched ? (
            loading ? (
                <div className="text-center py-5">
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                </div>
            ) : report.length === 0 ? (
                <Card className="shadow-sm border-0">
                    <Card.Body className="text-center py-5">
                        <div className="mb-3 fs-1 text-muted">📊</div>
                        <p className="text-muted mb-0">{t('No data found for the selected vendor and month')}</p>
                    </Card.Body>
                </Card>
            ) : (
                report.map((vendor) => {
                    const summaryTotals = calculateSummaryTotals(vendor);
                    return (
                        <div key={vendor.vendor_id} className="mb-4">
                            {/* Table 1: Vendor Summary */}
                            <Card className="shadow-sm mb-3 border-0">
                                <Card.Header className="bg-primary text-white py-3">
                                    <div className="d-flex justify-content-between align-items-center">
                                        <div>
                                            <FaStore className="me-2" />
                                            <strong>{vendor.vendor_name}</strong>
                                            <small className="ms-2 text-white-50">
                                                {t('Total Products Sold')}: {vendor.total_products_sold}
                                            </small>
                                        </div>
                                        <Button
                                            variant="light"
                                            size="sm"
                                            onClick={() => toggleVendor(vendor.vendor_id)}
                                            className="rounded-circle"
                                        >
                                            {expandedVendors[vendor.vendor_id] ? <FaChevronUp /> : <FaChevronDown />}
                                        </Button>
                                    </div>
                                </Card.Header>

                                {expandedVendors[vendor.vendor_id] && (
                                    <Card.Body className="p-0">
                                        {/* Table 1: Vendor Summary Table */}
                                        <div className="table-responsive">
                                            <Table className="mb-0">
                                                <thead className="table-light">
                                                    <tr>
                                                        <th className="p-3">{t('Total Products Sold')}</th>
                                                        <th className="p-3 text-end">{t('Total Purchase')}</th>
                                                        <th className="p-3 text-end">{t('Total Sales')}</th>
                                                        <th className="p-3 text-end">{t('Total Profit')}</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    <tr>
                                                        <td className="p-3 fw-bold fs-4">{vendor.total_products_sold}</td>
                                                        <td className="p-3 text-end text-primary fw-bold fs-5">{formatCurrency(vendor.total_purchase)}</td>
                                                        <td className="p-3 text-end text-success fw-bold fs-5">{formatCurrency(vendor.total_sales)}</td>
                                                        <td className="p-3 text-end">
                                                            <span className={`badge ${vendor.total_profit >= 0 ? 'bg-success' : 'bg-danger'} fs-6 px-3 py-2`}>
                                                                {formatCurrency(vendor.total_profit)}
                                                            </span>
                                                        </td>
                                                    </tr>
                                                </tbody>
                                            </Table>
                                        </div>

                                        {/* Table 2: Product-wise Summary */}
                                        {vendor.products && vendor.products.length > 0 && (
                                            <div className="mt-4 px-3">
                                                <h6 className="fw-bold mb-3 text-primary">{t('Product-wise Summary')}</h6>
                                                <div className="table-responsive">
                                                    <Table hover className="mb-0">
                                                        <thead className="table-light">
                                                            <tr>
                                                                <th className="p-3" style={{ width: '5%' }}>#</th>
                                                                <th className="p-3">{t('Product Name')}</th>
                                                                <th className="p-3 text-end">{t('Items Sold')}</th>
                                                                <th className="p-3 text-end">{t('Purchase Amount')}</th>
                                                                <th className="p-3 text-end">{t('Sale Amount')}</th>
                                                                <th className="p-3 text-end">{t('Profit')}</th>
                                                                <th className="p-3 text-center" style={{ width: '5%' }}></th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {vendor.products.map((product, idx) => (
                                                                <React.Fragment key={idx}>
                                                                    <tr>
                                                                        <td className="p-3 text-center">{idx + 1}</td>
                                                                        <td className="p-3 fw-semibold">{product.product_name}</td>
                                                                        <td className="p-3 text-end fw-semibold">{product.total_items_sold}</td>
                                                                        <td className="p-3 text-end text-primary">{formatCurrency(product.total_purchase_amount)}</td>
                                                                        <td className="p-3 text-end text-success">{formatCurrency(product.total_sale_amount)}</td>
                                                                        <td className="p-3 text-end">
                                                                            <span className={`badge ${product.profit >= 0 ? 'bg-success' : 'bg-danger'}`}>
                                                                                {formatCurrency(product.profit)}
                                                                            </span>
                                                                        </td>
                                                                        <td className="p-3 text-center">
                                                                            <Button
                                                                                variant="link"
                                                                                size="sm"
                                                                                onClick={() => toggleProduct(vendor.vendor_id, product.product_name)}
                                                                                className="text-primary p-0"
                                                                            >
                                                                                {expandedProducts[`${vendor.vendor_id}_${product.product_name}`] ? <FaChevronUp /> : <FaChevronDown />}
                                                                            </Button>
                                                                        </td>
                                                                    </tr>
                                                                    {/* Table 3: Detailed Summary for this product */}
                                                                    {expandedProducts[`${vendor.vendor_id}_${product.product_name}`] && vendor.summary && (
                                                                        <tr>
                                                                            <td colSpan="7" className="p-0">
                                                                                <div className="bg-light p-3 ms-4 me-4 mb-2 rounded">
                                                                                    <div className="table-responsive">
                                                                                        <Table size="sm" className="mb-0">
                                                                                            <thead className="table-secondary">
                                                                                                <tr>
                                                                                                    <th className="p-2">{t('Sale Date')}</th>
                                                                                                    <th className="p-2">{t('Product Name')}</th>
                                                                                                    <th className="p-2 text-end">{t('Purchase Price')}</th>
                                                                                                    <th className="p-2 text-end">{t('Sale Price')}</th>
                                                                                                    <th className="p-2 text-end">{t('Profit')}</th>
                                                                                                </tr>
                                                                                            </thead>
                                                                                            <tbody>
                                                                                                {vendor.summary
                                                                                                    .filter(item => item.product_name === product.product_name)
                                                                                                    .map((item, itemIdx) => (
                                                                                                        <tr key={itemIdx}>
                                                                                                            <td className="p-2">{item.sale_date || '-'}</td>
                                                                                                            <td className="p-2">{item.product_name} ({item.customer_name || '-'})</td>
                                                                                                            <td className="p-2 text-end">{formatCurrency(item.purchase_price)}</td>
                                                                                                            <td className="p-2 text-end">{formatCurrency(item.sale_price)}</td>
                                                                                                            <td className="p-2 text-end">
                                                                                                                <span className={`badge ${item.profit >= 0 ? 'bg-success' : 'bg-danger'}`}>
                                                                                                                    {formatCurrency(item.profit)}
                                                                                                                </span>
                                                                                                            </td>
                                                                                                        </tr>
                                                                                                    ))}
                                                                                            </tbody>
                                                                                            <tfoot className="table-active">
                                                                                                <tr>
                                                                                                    <td colSpan="2" className="p-2 fw-bold">{t('Total for this product')}</td>
                                                                                                    <td className="p-2 text-end fw-bold text-primary">
                                                                                                        {formatCurrency(vendor.summary
                                                                                                            .filter(item => item.product_name === product.product_name)
                                                                                                            .reduce((sum, item) => sum + (item.purchase_price || 0), 0))}
                                                                                                    </td>
                                                                                                    <td className="p-2 text-end fw-bold text-success">
                                                                                                        {formatCurrency(vendor.summary
                                                                                                            .filter(item => item.product_name === product.product_name)
                                                                                                            .reduce((sum, item) => sum + (item.sale_price || 0), 0))}
                                                                                                    </td>
                                                                                                    <td className="p-2 text-end fw-bold">
                                                                                                        <span className={`badge ${product.profit >= 0 ? 'bg-success' : 'bg-danger'}`}>
                                                                                                            {formatCurrency(vendor.summary
                                                                                                                .filter(item => item.product_name === product.product_name)
                                                                                                                .reduce((sum, item) => sum + (item.profit || 0), 0))}
                                                                                                        </span>
                                                                                                    </td>
                                                                                                </tr>
                                                                                            </tfoot>
                                                                                        </Table>
                                                                                    </div>
                                                                                </div>
                                                                            </td>
                                                                        </tr>
                                                                    )}
                                                                </React.Fragment>
                                                            ))}
                                                        </tbody>
                                                        <tfoot className="table-active">
                                                            <tr>
                                                                <td colSpan="2" className="p-3 fw-bold fs-6">{t('GRAND TOTAL')}</td>
                                                                <td className="p-3 text-end fw-bold fs-6">{vendor.total_products_sold}</td>
                                                                <td className="p-3 text-end fw-bold text-primary fs-6">{formatCurrency(vendor.total_purchase)}</td>
                                                                <td className="p-3 text-end fw-bold text-success fs-6">{formatCurrency(vendor.total_sales)}</td>
                                                                <td className="p-3 text-end fw-bold fs-6">
                                                                    <span className={`badge ${vendor.total_profit >= 0 ? 'bg-success' : 'bg-danger'} fs-6 px-3 py-2`}>
                                                                        {formatCurrency(vendor.total_profit)}
                                                                    </span>
                                                                </td>
                                                                <td></td>
                                                            </tr>
                                                        </tfoot>
                                                    </Table>
                                                </div>
                                            </div>
                                        )}

                                        {/* Table 3: Detailed Summary (All items) */}
                                        {vendor.summary && vendor.summary.length > 0 && (
                                            <div className="mt-4 px-3 pb-3">
                                                <h6 className="fw-bold mb-3 text-primary">{t('Detailed Transaction Summary')}</h6>
                                                <div className="table-responsive">
                                                    <Table hover className="mb-0">
                                                        <thead className="table-light">
                                                            <tr>
                                                                <th className="p-3">#</th>
                                                                <th className="p-3">{t('Sale Date')}</th>
                                                                <th className="p-3">{t('Product Name')}</th>
                                                                <th className="p-3 text-end">{t('Purchase Price')}</th>
                                                                <th className="p-3 text-end">{t('Sale Price')}</th>
                                                                <th className="p-3 text-end">{t('Profit')}</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {vendor.summary.map((item, idx) => (
                                                                <tr key={idx}>
                                                                    <td className="p-3 text-center">{idx + 1}</td>
                                                                    <td className="p-3">{item.sale_date || '-'}</td>
                                                                    <td className="p-3">{item.product_name} ({item.customer_name || '-'})</td>
                                                                    <td className="p-3 text-end text-primary">{formatCurrency(item.purchase_price)}</td>
                                                                    <td className="p-3 text-end text-success">{formatCurrency(item.sale_price)}</td>
                                                                    <td className="p-3 text-end">
                                                                        <span className={`badge ${item.profit >= 0 ? 'bg-success' : 'bg-danger'}`}>
                                                                            {formatCurrency(item.profit)}
                                                                        </span>
                                                                    </td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                        <tfoot className="table-active">
                                                            <tr>
                                                                <td colSpan="3" className="p-3 fw-bold">{t('TOTAL')}</td>
                                                                <td className="p-3 text-end fw-bold text-primary">{formatCurrency(summaryTotals.totalPurchase)}</td>
                                                                <td className="p-3 text-end fw-bold text-success">{formatCurrency(summaryTotals.totalSale)}</td>
                                                                <td className="p-3 text-end fw-bold">
                                                                    <span className={`badge ${summaryTotals.totalProfit >= 0 ? 'bg-success' : 'bg-danger'} fs-6 px-3 py-2`}>
                                                                        {formatCurrency(summaryTotals.totalProfit)}
                                                                    </span>
                                                                </td>
                                                            </tr>
                                                        </tfoot>
                                                    </Table>
                                                </div>
                                            </div>
                                        )}
                                    </Card.Body>
                                )}
                            </Card>
                        </div>
                    );
                })
            )
        ) : (
            <Card className="shadow-sm border-0">
                <Card.Body className="text-center py-5">
                    <div className="mb-3 fs-1 text-muted">🔍</div>
                    <h5 className="text-muted mb-2">{t('Select a vendor to view report')}</h5>
                    <p className="text-muted small">{t('Please select a vendor and click search to view the report')}</p>
                </Card.Body>
            </Card>
        )}
    </PageLayout>
);
}